import { MoreHorizontal } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface MucThaoTac {
  nhan: string;
  onChon: () => void;
  /** Mục nguy hiểm (xoá) hiện màu đỏ và nằm cuối. */
  nguyHiem?: boolean;
  tat?: boolean;
}

export interface MenuThaoTacProps {
  muc: MucThaoTac[];
  /** Nhãn cho trình đọc màn hình, ví dụ `Thao tác với kênh Ancient Echoes`. */
  'aria-label': string;
  children?: ReactNode;
}

/**
 * Nút ⋯ gom các thao tác phụ của một hàng bảng.
 *
 * Bản cũ để 3–4 nút chữ trên mỗi hàng, nên cột cuối rộng hơn cột dữ liệu và
 * mắt phải bỏ qua cùng bốn chữ đó ở mọi hàng. Luật mới: **mỗi hàng tối đa một
 * nút chính**, phần còn lại vào đây.
 *
 * Đóng khi bấm ra ngoài hoặc bấm Esc — một menu không đóng được bằng Esc là
 * menu người dùng bàn phím bị kẹt trong đó.
 */
export function MenuThaoTac({ muc, 'aria-label': nhan }: MenuThaoTacProps) {
  const [mo, setMo] = useState(false);
  const boc = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mo) return;

    const bamNgoai = (e: MouseEvent) => {
      if (boc.current && !boc.current.contains(e.target as Node)) setMo(false);
    };
    const bamEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMo(false);
    };

    document.addEventListener('mousedown', bamNgoai);
    document.addEventListener('keydown', bamEsc);
    return () => {
      document.removeEventListener('mousedown', bamNgoai);
      document.removeEventListener('keydown', bamEsc);
    };
  }, [mo]);

  const sapXep = [...muc].sort((a, b) => Number(a.nguyHiem ?? false) - Number(b.nguyHiem ?? false));

  return (
    <div ref={boc} className="relative inline-block text-left">
      <button
        type="button"
        aria-label={nhan}
        aria-haspopup="menu"
        aria-expanded={mo}
        onClick={() => {
          setMo((x) => !x);
        }}
        className="vung-bam-nho inline-flex w-9 items-center justify-center rounded-o text-chu-phu hover:bg-neutral-nen hover:text-chu"
      >
        <MoreHorizontal size={18} aria-hidden="true" />
      </button>

      {mo && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 min-w-[180px] overflow-hidden rounded-o border border-vien bg-be-mat py-1 shadow-lg"
        >
          {sapXep.map((m) => (
            <button
              key={m.nhan}
              type="button"
              role="menuitem"
              disabled={m.tat ?? false}
              onClick={() => {
                setMo(false);
                m.onChon();
              }}
              className={`block w-full px-3 py-2 text-left text-co-ban disabled:cursor-not-allowed disabled:opacity-50 ${
                m.nguyHiem ? 'text-bad-chu hover:bg-bad-nen' : 'text-chu hover:bg-neutral-nen'
              }`}
            >
              {m.nhan}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
